/**
 * WaitlistQuestionFields - the practice questions in WaitlistForm's card
 * between the signup and the calendar. Role and practice size are pills, each
 * around a visually hidden radio button, so one tap answers them and the
 * arrow keys move through the pills like any radio group; picking Other as
 * the role adds a field to type it. On /ehr the current EHR is typed in.
 * Every question is required.
 */

import { useState } from "react";
import { ANSWER_MAX_LENGTH, OTHER, questionsFor, type AnswerField, type Question } from "./waitlist-questions";

interface WaitlistQuestionFieldsProps {
  askCurrentEhr: boolean;
  /** The field the form's error points at, if any. */
  invalid: string | null;
  errorId: string;
}

export function WaitlistQuestionFields({ askCurrentEhr, invalid, errorId }: WaitlistQuestionFieldsProps) {
  const [otherPicked, setOtherPicked] = useState<Partial<Record<Question["name"], boolean>>>({});
  const describe = (field: AnswerField) =>
    invalid === field ? { "aria-describedby": errorId, "aria-invalid": true } : {};

  return (
    <>
      {questionsFor(askCurrentEhr).map((q) =>
        q.kind === "choice" ? (
          <fieldset
            key={q.name}
            className="wl-q"
            aria-describedby={invalid === q.name ? errorId : undefined}
          >
            <legend className="wl-q-legend">{q.legend}</legend>
            <div className="wl-q-options">
              {q.options.map((option) => (
                <label key={option} className="wl-q-option">
                  <input
                    className="sr-only"
                    type="radio"
                    name={q.name}
                    value={option}
                    onChange={q.other ? () => setOtherPicked((p) => ({ ...p, [q.name]: option === OTHER })) : undefined}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
            {q.other && otherPicked[q.name] && (
              <label className="wl-q-other">
                <span className="sr-only">{q.other.label}</span>
                <input
                  className="wl-q-input"
                  name={q.other.name}
                  placeholder={q.other.label}
                  maxLength={ANSWER_MAX_LENGTH}
                  autoComplete="off"
                  {...describe(q.other.name)}
                />
              </label>
            )}
          </fieldset>
        ) : (
          <label key={q.name} className="wl-q">
            <span className="wl-q-legend">{q.legend}</span>
            <input
              className="wl-q-input"
              name={q.name}
              placeholder={q.placeholder}
              maxLength={ANSWER_MAX_LENGTH}
              autoComplete="off"
              {...describe(q.name)}
            />
          </label>
        ),
      )}

      <style>{`
        .wl-q {
          display: block;
          min-width: 0;
          margin: 0;
          padding: 0;
          border: 0;
        }
        .wl-q + .wl-q {
          margin-top: 24px;
        }
        /* Padding rather than margin: browsers lay a legend's margin out
           inconsistently. */
        .wl-q-legend {
          display: block;
          padding: 0 0 12px;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 15px;
          font-weight: 600;
          letter-spacing: -0.01em;
          line-height: 1.3;
          color: hsl(225 45% 14%);
        }
        .wl-q-options {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .wl-q-option {
          position: relative;
          display: inline-flex;
          cursor: pointer;
        }
        .wl-q-option span {
          display: inline-flex;
          align-items: center;
          height: 40px;
          padding: 0 16px;
          border-radius: 999px;
          border: 1px solid hsl(225 45% 14% / 0.14);
          font-family: 'Outfit', sans-serif;
          font-size: 15px;
          color: hsl(225 45% 14%);
          white-space: nowrap;
          transition: background-color 0.15s, border-color 0.15s, color 0.15s;
        }
        .wl-q-option:hover span {
          background: hsl(225 45% 14% / 0.04);
          border-color: hsl(225 45% 14% / 0.26);
        }
        .wl-q-option input:checked + span {
          background: hsl(225 45% 14%);
          border-color: hsl(225 45% 14%);
          color: hsl(38 30% 96%);
        }
        .wl-q-option input:focus-visible + span {
          box-shadow: 0 0 0 2px hsl(40 30% 98%), 0 0 0 4px hsl(var(--ring) / 0.5);
        }
        .wl-q-other {
          display: block;
          margin-top: 10px;
        }
        .wl-q-input {
          display: block;
          width: 100%;
          height: 46px;
          padding: 0 18px;
          border-radius: 999px;
          border: 1px solid hsl(225 45% 14% / 0.14);
          background: transparent;
          font-family: 'Outfit', sans-serif;
          font-size: 16px;
          color: hsl(225 45% 14%);
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .wl-q-input::placeholder {
          color: hsl(225 45% 14% / 0.5);
        }
        .wl-q-input:focus {
          border-color: transparent;
          box-shadow: 0 0 0 2px hsl(var(--ring) / 0.5);
        }
        /* The signup fields' error colours. */
        .wl-q-input[aria-invalid="true"] {
          border-color: hsl(var(--accent-rose-ink) / 0.7);
        }
        .wl-q-input[aria-invalid="true"]:focus {
          border-color: transparent;
          box-shadow: 0 0 0 2px hsl(var(--accent-rose-ink) / 0.5);
        }
        /* A finger-sized target on phones. */
        @media (max-width: 768px) {
          .wl-q-option span {
            height: 44px;
          }
        }
      `}</style>
    </>
  );
}
